import * as React from 'react'
import { ChevronLeft, ChevronRight, X } from 'lucide-react'
import { CommunityMessageAttachments } from '@/modules/comunidade/components/CommunityMessageAttachments'
import type { CommunityMessageAttachment } from '@/modules/comunidade/services/communityService'
import { isImageAttachment } from '@/modules/comunidade/utils/communityAttachments'
import type { AnalysisPostAttachment } from '../services/analysisPostAttachmentsService'

type AnalysisPostAttachmentsProps = {
  attachments: AnalysisPostAttachment[]
}

function normalizeAttachments(
  attachments: AnalysisPostAttachment[],
): CommunityMessageAttachment[] {
  const list = Array.isArray(attachments) ? attachments.filter((a) => a && a.url) : []
  return list.map((att) => ({
    id: att.id,
    messageId: att.analysisId,
    url: att.url,
    storageBucket: att.storageBucket,
    storagePath: att.storagePath,
    contentType: att.contentType,
    sizeBytes: att.sizeBytes,
    createdAt: att.createdAt,
  }))
}

export function AnalysisPostAttachments({ attachments }: AnalysisPostAttachmentsProps) {
  const normalized = React.useMemo(() => normalizeAttachments(attachments), [attachments])

  const [lightboxOpen, setLightboxOpen] = React.useState(false)
  const [lightboxAttachments, setLightboxAttachments] = React.useState<
    CommunityMessageAttachment[]
  >([])
  const [lightboxIndex, setLightboxIndex] = React.useState(0)

  const handleOpenLightbox = React.useCallback(
    (items: CommunityMessageAttachment[], index: number) => {
      const normalizedImages = Array.isArray(items)
        ? items.filter((a) => a && a.url && isImageAttachment(a))
        : []
      if (normalizedImages.length === 0) return

      const safeIndex = Number.isFinite(index)
        ? Math.max(0, Math.min(index, normalizedImages.length - 1))
        : 0

      setLightboxAttachments(normalizedImages)
      setLightboxIndex(safeIndex)
      setLightboxOpen(true)
    },
    [],
  )

  React.useEffect(() => {
    if (lightboxOpen) return
    setLightboxAttachments([])
    setLightboxIndex(0)
  }, [lightboxOpen])

  const hasLightbox = lightboxOpen && lightboxAttachments.length > 0
  const total = lightboxAttachments.length
  const currentIndex =
    hasLightbox && total > 0 ? Math.max(0, Math.min(lightboxIndex, total - 1)) : 0
  const current = hasLightbox && total > 0 ? lightboxAttachments[currentIndex] : null

  return (
    <>
      <CommunityMessageAttachments
        attachments={normalized}
        onOpenLightbox={handleOpenLightbox}
      />

      {hasLightbox && current && (
        <div className="fixed inset-0 z-[200000] flex items-center justify-center bg-black/80 px-4 py-6">
          <button
            type="button"
            onClick={() => setLightboxOpen(false)}
            className="absolute right-4 top-4 inline-flex items-center gap-2 rounded-full bg-black/60 px-3 py-1 text-xs font-medium text-white hover:bg-black/80"
          >
            <X className="h-4 w-4" />
            Fechar
          </button>

          {total > 1 && (
            <button
              type="button"
              onClick={() => {
                if (!hasLightbox || total <= 1) return
                setLightboxIndex((prev) => (prev <= 0 ? total - 1 : prev - 1))
              }}
              className="absolute left-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80"
              aria-label="Imagem anterior"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
          )}

          <div className="max-h-[80vh] max-w-[80vw]">
            <img
              src={current.url}
              alt="Imagem anexada à análise"
              className="h-full w-full rounded-lg object-contain shadow-2xl"
            />
          </div>

          {total > 1 && (
            <button
              type="button"
              onClick={() => {
                if (!hasLightbox || total <= 1) return
                setLightboxIndex((prev) => (prev >= total - 1 ? 0 : prev + 1))
              }}
              className="absolute right-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80"
              aria-label="Próxima imagem"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          )}

          {total > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-black/60 px-3 py-1 text-xs text-white">
              {currentIndex + 1} / {total}
            </div>
          )}
        </div>
      )}
    </>
  )
}

